/**
 * An API error as a sentence in the active language.
 *
 * The API's `detail` is English written for developers (docs/help/errors.md
 * keeps it out of messages). Codes with approved copy in the error catalog
 * are translated here; for any other code English keeps the server's detail,
 * as before, and other languages get a plain "did not finish" with Try again.
 * Duck-typed on { code, detail } so this module does not import the API
 * client (which imports lib/auth/messages, which imports this layer).
 */
import type { MessageKey, TFunction } from "./types";

/** Product limits quoted in the copy (kernel/identity/auth.service.ts). */
export const SIGN_IN_LIMITS = {
  attempts: 5,
  lockMinutes: 15,
  rateLimit: 20,
  rateWindowMinutes: 5,
  mfaWindowMinutes: 5,
} as const;

/**
 * Sign-up and e-mail verification limits quoted in the copy
 * (kernel/identity/password.service.ts MIN_PASSWORD_LENGTH and
 * validateStrength; auth.service.ts verifyEmail).
 */
export const SIGN_UP_LIMITS = {
  minPassword: 12,
  maxPassword: 1024,
  codeDigits: 6,
  codeMinutes: 30,
} as const;

/**
 * Session limits quoted in the session codes' copy (kernel/identity/
 * session.service.ts: renewal stops after 12 hours without use, and 7 days
 * after signing in; docs/design/tokens.md section 2.3).
 */
export const SESSION_LIMITS = { idleHours: 12, maxDays: 7 } as const;

/**
 * The session, token and guard codes S2-05f added to ERROR_CODES, each with
 * one sentence of copy (docs/help/errors.md, the people-in-the-app row).
 */
const SESSION_CODES: ReadonlySet<string> = new Set([
  "TOKEN_EXPIRED",
  "TOKEN_INVALID",
  "NO_SESSION",
  "SESSION_EXPIRED",
  "SESSION_REVOKED",
  "NOT_A_USER_SESSION",
  "UNSUPPORTED_MEDIA_TYPE",
  "SIGNING_KEYS_UNAVAILABLE",
  "VERIFICATION_PEPPER_UNAVAILABLE",
  "REFRESH_TOKEN_USED",
  "REFRESH_TOKEN_UPDATE_MUST_USE",
  "REFRESH_TOKEN_REWRITE",
  "SESSION_OWNER_MISMATCH",
  "RATE_LIMIT_UNAVAILABLE",
  "CONFIGURATION_INVALID",
]);

/**
 * VALIDATION_FAILED is one code for hundreds of rules. The sign-up and
 * verification ones have approved copy (docs/help/errors.md, the
 * VALIDATION_FAILED table) and are told apart by the detail, as
 * AUTHENTICATION_FAILED is below, until the API sends a code per rule (D22).
 */
const VALIDATION_DETAILS: Array<[prefix: string, key: MessageKey]> = [
  ["A valid email address is required", "errors.VALIDATION_FAILED.email"],
  ["Password must be at least", "errors.VALIDATION_FAILED.passwordTooShort"],
  ["Password must be at most", "errors.VALIDATION_FAILED.passwordTooLong"],
  [
    "Password must not start or end with whitespace",
    "errors.VALIDATION_FAILED.passwordSpace",
  ],
  [
    "A 6-digit verification code is required",
    "errors.VALIDATION_FAILED.codeFormat",
  ],
  [
    "That verification code is invalid or has expired",
    "errors.VALIDATION_FAILED.codeInvalid",
  ],
];

/** AUTHENTICATION_FAILED shares one code; the detail tells the cases apart. */
const AUTH_DETAILS: Array<[prefix: string, key: MessageKey]> = [
  [
    "Email or password is incorrect",
    "errors.AUTHENTICATION_FAILED.credentials",
  ],
  [
    "This account requires a second factor",
    "errors.AUTHENTICATION_FAILED.noFactor",
  ],
  ["That code is not valid", "errors.AUTHENTICATION_FAILED.codeInvalid"],
  ["That code has already been used", "errors.AUTHENTICATION_FAILED.codeUsed"],
  [
    "This sign-in attempt has expired",
    "errors.AUTHENTICATION_FAILED.attemptExpired",
  ],
];

interface ErrorLike {
  code?: unknown;
  detail?: unknown;
  message?: unknown;
  requestId?: unknown;
}

export function errorMessage(
  err: unknown,
  t: TFunction,
  language: string,
): string {
  const e = (err ?? {}) as ErrorLike;
  const code = typeof e.code === "string" ? e.code : undefined;
  const detail =
    typeof e.detail === "string" && e.detail !== ""
      ? e.detail
      : typeof e.message === "string"
        ? e.message
        : "";
  const params = {
    attempts: SIGN_IN_LIMITS.attempts,
    limit: SIGN_IN_LIMITS.rateLimit,
  };
  switch (code) {
    case "AUTHENTICATION_FAILED": {
      const key =
        AUTH_DETAILS.find(([prefix]) => detail.startsWith(prefix))?.[1] ??
        "errors.AUTHENTICATION_FAILED.credentials";
      const minutes =
        key === "errors.AUTHENTICATION_FAILED.attemptExpired"
          ? SIGN_IN_LIMITS.mfaWindowMinutes
          : SIGN_IN_LIMITS.lockMinutes;
      return t(key, { ...params, minutes });
    }
    case "VALIDATION_FAILED": {
      const key = VALIDATION_DETAILS.find(([prefix]) =>
        detail.startsWith(prefix),
      )?.[1];
      if (key)
        return t(key, {
          min: SIGN_UP_LIMITS.minPassword,
          max: SIGN_UP_LIMITS.maxPassword,
          digits: SIGN_UP_LIMITS.codeDigits,
          minutes: SIGN_UP_LIMITS.codeMinutes,
        });
      break;
    }
    case "RATE_LIMITED":
      return t("errors.RATE_LIMITED", {
        ...params,
        minutes: SIGN_IN_LIMITS.rateWindowMinutes,
      });
    case "NETWORK_ERROR":
      return t("errors.NETWORK_ERROR");
    case "UNKNOWN":
      return t("errors.UNKNOWN");
  }
  if (code !== undefined && SESSION_CODES.has(code))
    return t(`errors.${code}`, {
      defaultValue: detail || t("errors.fallback"),
      requestId:
        typeof e.requestId === "string" && e.requestId !== ""
          ? e.requestId
          : "—",
      idleHours: SESSION_LIMITS.idleHours,
      days: SESSION_LIMITS.maxDays,
    });
  if (language === "en" && detail) return detail;
  return t("errors.fallback");
}
